import React from 'react'
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material'

/**
 * DataTable — a light, theme-consistent table matching the admin panel's
 * grid look (uppercase secondary headers, subtle zebra, divider borders).
 *
 * columns: [{ key, label, align, width, render(row) }]
 * rows: array of objects
 * onRowClick: optional (row) => void  (rows become clickable)
 */
export default function DataTable({ columns = [], rows = [], onRowClick, getRowKey, dense = true, emptyText = 'No records', minWidth = 480, dotted = false, bare = false }) {
  const bs = dotted ? 'dotted' : 'solid'
  return (
    <TableContainer sx={{ border: bare ? 'none' : `1px ${bs}`, borderColor: '#E7EAF0', borderRadius: bare ? 0 : '10px', overflow: 'auto', boxShadow: bare ? 'none' : '0 1px 3px rgba(0,0,0,0.05)' }}>
      <Table size={dense ? 'small' : 'medium'} sx={{ minWidth }}>
        <TableHead>
          <TableRow sx={{ bgcolor: '#F9FAFB' }}>
            {columns.map((c) => (
              <TableCell
                key={c.key}
                align={c.align || 'left'}
                sx={{
                  fontWeight: 700,
                  fontSize: 11,
                  color: '#475569',
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em',
                  whiteSpace: 'nowrap',
                  borderBottom: `1px ${bs}`,
                  borderColor: '#E2E8F0',
                  width: c.width,
                }}
              >
                {c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 3, color: 'text.secondary', fontSize: 12 }}>
                {emptyText}
              </TableCell>
            </TableRow>
          )}
          {rows.map((row, idx) => (
            <TableRow
              key={getRowKey ? getRowKey(row, idx) : row.id || idx}
              hover={!!onRowClick}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              sx={{
                cursor: onRowClick ? 'pointer' : 'default',
                transition: 'background-color 0.15s ease',
                '&:nth-of-type(even)': { bgcolor: '#FAFBFC' },
                '&:last-child td': { borderBottom: 0 },
              }}
            >
              {columns.map((c) => (
                <TableCell
                  key={c.key}
                  align={c.align || 'left'}
                  sx={{ fontSize: 13, color: 'text.primary', borderBottom: `1px ${bs}`, borderColor: '#E8ECF1', py: dense ? 1.25 : 1.5 }}
                >
                  {c.render ? c.render(row) : row[c.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

/**
 * RateCardHeatmap — weight-slab × zone margin grid with coloured cells.
 * data: { zones: [...], rows: [{ wt, cells: [{ v, cls }] }] }
 * cls → g2 (strong green) | g (green) | y (yellow) | r (red)
 */
const CELL = {
  g2: { bg: '#BBF7D0', color: '#14532D' },
  g: { bg: '#DCFCE7', color: '#166534' },
  y: { bg: '#FEF9C3', color: '#854D0E' },
  r: { bg: '#FEE2E2', color: '#991B1B' },
}

export function RateCardHeatmap({ data, firstColLabel = 'Wt \\ Zone' }) {
  if (!data) return null
  return (
    <TableContainer sx={{ border: '1px solid', borderColor: '#E7EAF0', borderRadius: '10px', overflow: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: '#F9FAFB' }}>
            <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em' }}>{firstColLabel}</TableCell>
            {data.zones.map((z) => (
              <TableCell key={z} align="center" sx={{ fontWeight: 700, fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em' }}>{z}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.rows.map((r) => (
            <TableRow key={r.wt}>
              <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{r.wt}</TableCell>
              {r.cells.map((c, i) => {
                const s = CELL[c.cls] || CELL.g
                return (
                  <TableCell key={i} align="center" sx={{ fontWeight: 700, fontSize: 12, bgcolor: s.bg, color: s.color, borderColor: '#fff' }}>
                    {c.v}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

/** HeatLegend — colour key for the heat-map. */
export function HeatLegend() {
  const items = [
    { c: '#DCFCE7', t: 'Strong lane' },
    { c: '#FEF9C3', t: 'Watch' },
    { c: '#FEE2E2', t: 'Weak / loss lane' },
  ]
  return (
    <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
      {items.map((i) => (
        <Box key={i.t} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: i.c }} />
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{i.t}</Typography>
        </Box>
      ))}
    </Box>
  )
}
